import { useEffect, useMemo, useState } from 'preact/hooks';
import { useI18n } from '../i18n';
import { RequireAdmin } from '../components/RequireAdmin';
import { useAuth } from '../lib/auth';
import {
  eraseVoucher,
  giftDrinkToUser,
  listAdminUsers,
  listPartnersAdmin,
  listUserVouchers,
  unEraseVoucher,
  updateAdminUser,
  type AdminUser,
  type AdminUsersFilter,
  type AdminVoucher,
  type DrinkSize,
  type Locale,
  type Partner,
  type UserRole,
  type UserStatus,
} from '../lib/admin';

const ROLES: UserRole[] = ['user', 'partner', 'admin'];
const STATUSES: UserStatus[] = ['active', 'suspended', 'deleted'];
const LOCALES: Locale[] = ['en', 'de', 'fr', 'it'];

// Hardcoded to mirror AdminPartners — Swiss cantons in canonical FSO order.
// Used as the canton filter dropdown; the user profile stores the 2-letter
// code so we filter on that, not on the localised name.
const CANTONS: Array<{ code: string; name: string }> = [
  { code: 'ZH', name: 'Zürich' },        { code: 'BE', name: 'Bern' },
  { code: 'LU', name: 'Lucerne' },       { code: 'UR', name: 'Uri' },
  { code: 'SZ', name: 'Schwyz' },        { code: 'OW', name: 'Obwalden' },
  { code: 'NW', name: 'Nidwalden' },     { code: 'GL', name: 'Glarus' },
  { code: 'ZG', name: 'Zug' },           { code: 'FR', name: 'Fribourg' },
  { code: 'SO', name: 'Solothurn' },     { code: 'BS', name: 'Basel-Stadt' },
  { code: 'BL', name: 'Basel-Landschaft' },{ code: 'SH', name: 'Schaffhausen' },
  { code: 'AR', name: 'Appenzell Ausserrhoden' },
  { code: 'AI', name: 'Appenzell Innerrhoden' },
  { code: 'SG', name: 'St. Gallen' },    { code: 'GR', name: 'Graubünden' },
  { code: 'AG', name: 'Aargau' },        { code: 'TG', name: 'Thurgau' },
  { code: 'TI', name: 'Ticino' },        { code: 'VD', name: 'Vaud' },
  { code: 'VS', name: 'Valais' },        { code: 'NE', name: 'Neuchâtel' },
  { code: 'GE', name: 'Geneva' },        { code: 'JU', name: 'Jura' },
];

const PAGE_SIZE = 50;

export function AdminUsers() {
  const { localePath } = useI18n();
  const { accessToken, user: me } = useAuth();
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [role, setRole] = useState<UserRole | ''>('');
  const [status, setStatus] = useState<UserStatus | ''>('');
  const [canton, setCanton] = useState('');
  const [offset, setOffset] = useState(0);
  const [data, setData] = useState<{ users: AdminUser[]; total: number } | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [editing, setEditing] = useState<AdminUser | null>(null);
  const [gifting, setGifting] = useState<AdminUser | null>(null);
  const [viewingVouchers, setViewingVouchers] = useState<AdminUser | null>(null);
  const [partners, setPartners] = useState<Partner[]>([]);

  // Partner directory (admin-listed, includes inactive) so the edit modal
  // can map partner_id → name and offer the picker. One-shot fetch; the
  // partner directory is tiny and rarely changes.
  useEffect(() => {
    if (!accessToken) return;
    listPartnersAdmin(accessToken)
      .then(setPartners)
      .catch(() => {/* non-fatal — picker just shows raw ids */});
  }, [accessToken]);

  // Debounce free-text search so we don't fire a request on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 250);
    return () => clearTimeout(t);
  }, [search]);

  // Any filter change resets pagination — otherwise you can land on offset=200
  // of a 30-row filter result and see "no users" by accident.
  useEffect(() => setOffset(0), [debouncedSearch, role, status, canton]);

  const filter: AdminUsersFilter = useMemo(
    () => ({
      search: debouncedSearch || undefined,
      role: role || undefined,
      status: status || undefined,
      canton: canton || undefined,
      limit: PAGE_SIZE,
      offset,
    }),
    [debouncedSearch, role, status, canton, offset],
  );

  async function load() {
    if (!accessToken) return;
    setLoading(true);
    setErr(null);
    try {
      const res = await listAdminUsers(accessToken, filter);
      setData({ users: res.users, total: res.total });
    } catch (e: any) {
      setErr(`${e?.message || e}`);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, [accessToken, filter.search, filter.role, filter.status, filter.canton, filter.offset]);

  function clearFilters() {
    setSearch('');
    setRole('');
    setStatus('');
    setCanton('');
  }

  const total = data?.total ?? 0;
  const showing = data?.users.length ?? 0;
  const from = total === 0 ? 0 : offset + 1;
  const to = offset + showing;

  return (
    <RequireAdmin>
      <div class="admin-page">
        <div class="admin-shell">
          <header class="admin-header">
            <a class="admin-back" href={localePath('/admin')}>← Admin</a>
            <h1>Users</h1>
            <div class="admin-who" style={{ marginLeft: 'auto' }}>
              <span class="admin-mono">{total} total</span>
            </div>
          </header>

          <p class="admin-lede">
            Browse, filter and edit every signed-up account. Edits touch role
            (privileges), status (account state) and locale only. Email and
            profile fields are owned by the user.
          </p>

          <div class="admin-filter-row">
            <input
              class="admin-filter-input"
              type="search"
              placeholder="Search email or nickname…"
              value={search}
              onInput={(e) => setSearch((e.target as HTMLInputElement).value)}
            />
            <select
              class="admin-filter-select"
              value={role}
              onChange={(e) => setRole((e.target as HTMLSelectElement).value as UserRole | '')}
            >
              <option value="">All roles</option>
              {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
            <select
              class="admin-filter-select"
              value={status}
              onChange={(e) => setStatus((e.target as HTMLSelectElement).value as UserStatus | '')}
            >
              <option value="">All statuses</option>
              {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <select
              class="admin-filter-select"
              value={canton}
              onChange={(e) => setCanton((e.target as HTMLSelectElement).value)}
            >
              <option value="">All cantons</option>
              {CANTONS.map((c) => <option key={c.code} value={c.code}>{c.code} · {c.name}</option>)}
            </select>
            <button type="button" class="admin-link" onClick={clearFilters}>Reset</button>
          </div>

          {err && <p class="admin-error">{err}</p>}
          {loading && !data && <p class="admin-lede">Loading…</p>}

          {data && data.users.length === 0 && !loading && (
            <p class="admin-lede">No users match these filters.</p>
          )}

          {data && data.users.length > 0 && (
            <>
              <table class="admin-table">
                <thead>
                  <tr>
                    <th>Email</th>
                    <th>Nickname</th>
                    <th>Role</th>
                    <th>Partner</th>
                    <th>Status</th>
                    <th>Canton</th>
                    <th>Locale</th>
                    <th>Last login</th>
                    <th>Created</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {data.users.map((u) => (
                    <tr key={u.id} style={{ opacity: u.status === 'deleted' ? 0.5 : 1 }}>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <Avatar url={u.avatarUrl} email={u.email} />
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              <span>{u.email}</span>
                              {u.emailVerified && (
                                <span title="email verified" style={{ color: '#5BBA5B' }}>✓</span>
                              )}
                              {me?.id === u.id && (
                                <span class="admin-mono" style={{ fontSize: 9, color: '#E87B1E' }}>YOU</span>
                              )}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td>{u.nickname ?? <span class="admin-mono" style={{ opacity: 0.4 }}>—</span>}</td>
                      <td><RolePill role={u.role} /></td>
                      <td>
                        {u.partnerName ? (
                          <span class="admin-mono" style={{ fontSize: 11 }}>{u.partnerName}</span>
                        ) : (
                          u.role === 'partner'
                            ? <span style={{ color: '#E8B53C', fontSize: 11 }}>⚠ unlinked</span>
                            : <span style={{ opacity: 0.4 }}>—</span>
                        )}
                      </td>
                      <td><StatusPill status={u.status} /></td>
                      <td>{u.canton ?? '—'}</td>
                      <td class="admin-mono">{u.locale}</td>
                      <td class="admin-mono">{u.lastLoginAt ? fmtDate(u.lastLoginAt) : '—'}</td>
                      <td class="admin-mono">{fmtDate(u.createdAt)}</td>
                      <td>
                        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                          <button
                            type="button"
                            class="admin-link"
                            title="Gift a drink voucher to this user for testing the partner flow"
                            onClick={() => setGifting(u)}
                          >
                            Gift
                          </button>
                          <button
                            type="button"
                            class="admin-link"
                            title="Browse this user's vouchers and erase/undo for testing"
                            onClick={() => setViewingVouchers(u)}
                          >
                            Vouchers
                          </button>
                          <button class="admin-link" onClick={() => setEditing(u)}>Edit</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div class="admin-pager">
                <span class="admin-mono">
                  {from}–{to} of {total}
                </span>
                <div class="admin-pager-buttons">
                  <button
                    class="admin-link"
                    disabled={offset === 0}
                    onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                  >
                    ← Prev
                  </button>
                  <button
                    class="admin-link"
                    disabled={to >= total}
                    onClick={() => setOffset(offset + PAGE_SIZE)}
                  >
                    Next →
                  </button>
                </div>
              </div>
            </>
          )}

          {gifting && accessToken && (
            <GiftDrinkModal
              user={gifting}
              accessToken={accessToken}
              onClose={() => setGifting(null)}
            />
          )}

          {viewingVouchers && accessToken && (
            <UserVouchersModal
              user={viewingVouchers}
              accessToken={accessToken}
              onClose={() => setViewingVouchers(null)}
            />
          )}

          {editing && accessToken && (
            <EditUserModal
              user={editing}
              accessToken={accessToken}
              selfId={me?.id ?? ''}
              partners={partners}
              onClose={() => setEditing(null)}
              onSaved={(updated) => {
                setEditing(null);
                setData((cur) =>
                  cur
                    ? { ...cur, users: cur.users.map((u) => (u.id === updated.id ? updated : u)) }
                    : cur,
                );
              }}
            />
          )}
        </div>
      </div>
    </RequireAdmin>
  );
}

function Avatar({ url, email }: { url?: string; email: string }) {
  // Fallback shows the first letter of the email, monogram-style. Keeps the
  // table row height consistent even when a user has no avatar_url yet
  // (magic-link signups without a Google/Apple profile picture).
  const initial = (email[0] || '?').toUpperCase();
  if (url) {
    return (
      <img
        src={url}
        alt=""
        referrerPolicy="no-referrer"
        style={{
          width: 28,
          height: 28,
          borderRadius: '50%',
          objectFit: 'cover',
          background: '#0e0c0a',
          border: '1px solid rgba(245,237,224,0.1)',
        }}
      />
    );
  }
  return (
    <div
      style={{
        width: 28,
        height: 28,
        borderRadius: '50%',
        background: 'rgba(232,123,30,0.18)',
        color: '#E87B1E',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 12,
        fontWeight: 700,
        border: '1px solid rgba(232,123,30,0.35)',
      }}
    >
      {initial}
    </div>
  );
}

function fmtDate(iso: string): string {
  // Compact ISO: drop seconds + zone for table density.
  const m = iso.match(/^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})/);
  return m ? `${m[1]} ${m[2]}` : iso;
}

function RolePill({ role }: { role: UserRole }) {
  const color = role === 'admin' ? '#E87B1E' : role === 'partner' ? '#F5A04B' : '#9EA8B5';
  return (
    <span
      class="admin-mono"
      style={{
        fontSize: 10,
        letterSpacing: '0.12em',
        textTransform: 'uppercase',
        padding: '2px 6px',
        borderRadius: 999,
        border: `1px solid ${color}`,
        color,
      }}
    >
      {role}
    </span>
  );
}

function StatusPill({ status }: { status: UserStatus }) {
  const color =
    status === 'active' ? '#5BBA5B' : status === 'suspended' ? '#E8B53C' : '#C0392B';
  return (
    <span
      class="admin-mono"
      style={{
        fontSize: 10,
        letterSpacing: '0.12em',
        textTransform: 'uppercase',
        padding: '2px 6px',
        borderRadius: 999,
        background: `${color}22`,
        color,
      }}
    >
      {status}
    </span>
  );
}

function EditUserModal({
  user,
  accessToken,
  selfId,
  partners,
  onClose,
  onSaved,
}: {
  user: AdminUser;
  accessToken: string;
  selfId: string;
  partners: Partner[];
  onClose: () => void;
  onSaved: (u: AdminUser) => void;
}) {
  const [role, setRole] = useState<UserRole>(user.role);
  const [status, setStatus] = useState<UserStatus>(user.status);
  const [locale, setLocale] = useState<Locale>(user.locale);
  const [nickname, setNickname] = useState(user.nickname ?? '');
  const [canton, setCanton] = useState(user.canton ?? '');
  // partnerId state: number (>0) = pick a partner; '' = unchanged (only
  // when user already has none); 'clear' = explicit clear. To keep the
  // select simple we collapse "unchanged" and "clear" into the empty
  // option and rely on `originalPartnerId` to decide what to send.
  const [partnerId, setPartnerId] = useState<number | ''>(user.partnerId ?? '');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const isSelf = selfId === user.id;
  const willDemoteSelf = isSelf && user.role === 'admin' && role !== 'admin';
  const promotingToPartner = role === 'partner' && partnerId === '';

  async function submit(e: Event) {
    e.preventDefault();
    if (willDemoteSelf) {
      setErr("Can't remove your own admin role — ask another admin.");
      return;
    }
    setSaving(true);
    setErr(null);
    try {
      // Diff against the original record so we only send fields that
      // actually changed. The backend treats omitted fields as "unchanged".
      const nicknameTrim = nickname.trim();
      const cantonTrim = canton.trim();
      const orig = {
        role: user.role,
        status: user.status,
        locale: user.locale,
        nickname: user.nickname ?? '',
        canton: user.canton ?? '',
        partnerId: user.partnerId ?? null,
      };
      // partner_id mapping:
      //   pick '' + had one  → null (clear)
      //   pick a number     → that number
      //   pick '' + had none → unchanged (omit)
      let partnerIdPayload: number | null | undefined = undefined;
      if (partnerId === '' && orig.partnerId != null) partnerIdPayload = null;
      else if (typeof partnerId === 'number' && partnerId !== orig.partnerId) partnerIdPayload = partnerId;

      const updated = await updateAdminUser(accessToken, user.id, {
        role: role !== orig.role ? role : undefined,
        status: status !== orig.status ? status : undefined,
        locale: locale !== orig.locale ? locale : undefined,
        nickname: nicknameTrim !== orig.nickname ? nicknameTrim : undefined,
        canton: cantonTrim !== orig.canton ? cantonTrim : undefined,
        partnerId: partnerIdPayload,
      });
      onSaved(updated);
    } catch (e: any) {
      // Surface the 409 nickname-taken message inline rather than a generic
      // failure so the admin sees what to fix.
      const msg = e?.code === 'nickname_taken'
        ? 'That nickname is already taken — pick another.'
        : e?.message || 'Save failed';
      setErr(msg);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div class="admin-overlay" onClick={onClose}>
      <form
        class="admin-modal"
        style={{ width: 'min(560px, 100%)' }}
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
      >
        <h2 style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Avatar url={user.avatarUrl} email={user.email} />
          <span>Edit user</span>
        </h2>

        <dl class="admin-meta">
          <div>
            <dt>ID</dt>
            <dd class="admin-mono" style={{ fontSize: 11 }}>{user.id}</dd>
          </div>
          <div>
            <dt>Email</dt>
            <dd>{user.email} {user.emailVerified && <span style={{ color: '#5BBA5B' }}>✓</span>}</dd>
          </div>
          <div>
            <dt>Created</dt>
            <dd class="admin-mono" style={{ fontSize: 11 }}>{user.createdAt}</dd>
          </div>
        </dl>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <label class="admin-field">
            <span>Nickname</span>
            <input
              value={nickname}
              onInput={(e) => setNickname((e.target as HTMLInputElement).value)}
              placeholder="caplover"
              autocomplete="off"
              spellcheck={false}
            />
          </label>
          <label class="admin-field">
            <span>Canton</span>
            <select value={canton} onChange={(e) => setCanton((e.target as HTMLSelectElement).value)}>
              <option value="">— none</option>
              {CANTONS.map((c) => (
                <option key={c.code} value={c.code}>{c.code} · {c.name}</option>
              ))}
            </select>
          </label>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
          <label class="admin-field">
            <span>Role</span>
            <select value={role} onChange={(e) => setRole((e.target as HTMLSelectElement).value as UserRole)}>
              {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </label>
          <label class="admin-field">
            <span>Status</span>
            <select value={status} onChange={(e) => setStatus((e.target as HTMLSelectElement).value as UserStatus)}>
              {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </label>
          <label class="admin-field">
            <span>Locale</span>
            <select value={locale} onChange={(e) => setLocale((e.target as HTMLSelectElement).value as Locale)}>
              {LOCALES.map((l) => <option key={l} value={l}>{l}</option>)}
            </select>
          </label>
        </div>

        <label class="admin-field">
          <span>Linked partner {role === 'partner' && <span style={{ color: '#E8B53C' }}>★ required for partner role</span>}</span>
          <select
            value={partnerId === '' ? '' : String(partnerId)}
            onChange={(e) => {
              const v = (e.target as HTMLSelectElement).value;
              setPartnerId(v === '' ? '' : Number(v));
            }}
          >
            <option value="">— not linked</option>
            {partners.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}{p.canton ? ` · ${p.canton}` : ''}{p.active ? '' : ' (inactive)'}
              </option>
            ))}
          </select>
        </label>

        {willDemoteSelf && (
          <p class="admin-error">
            You're editing your own account. Removing the admin role here would lock you out — change another admin's role first, or ask one to demote you.
          </p>
        )}
        {promotingToPartner && (
          <p class="admin-error" style={{ color: '#E8B53C' }}>
            Heads-up: role is "partner" but no partner is linked. The user won't be able to access their partner self-service page until you pick one.
          </p>
        )}
        {err && <p class="admin-error">{err}</p>}

        <div class="admin-modal-actions">
          <button type="button" class="admin-link" onClick={onClose}>Cancel</button>
          <button
            type="submit"
            class="admin-primary"
            disabled={saving || willDemoteSelf}
          >
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </form>
    </div>
  );
}

// ─── Gift drink modal ────────────────────────────────────────────────────
//
// Mints a redeemable drink voucher attached to this user. The voucher
// behaves identically to a draw-issued one — same QR token, same
// partner-scan path — but skips the prediction-lottery step so we can
// hand-walk a known user through the end-to-end partner flow.
//
// matchNumber is optional. Leaving it blank gifts a "free" drink
// (match_id NULL) so the same user can be gifted repeatedly without
// hitting the (match_id, user_id) uniqueness constraint.

const DRINK_SIZES: { size: DrinkSize; label: string }[] = [
  { size: 'third', label: '0.33L (third)' },
  { size: 'half', label: '0.5L (half)' },
  { size: 'pint', label: 'Pint' },
];

function GiftDrinkModal({
  user,
  accessToken,
  onClose,
}: {
  user: AdminUser;
  accessToken: string;
  onClose: () => void;
}) {
  // Default to the live tournament. Hard-coded today because there's
  // only one live tournament and adding a tournament picker for a
  // one-off test tool would be premature.
  const [tournamentCode, setTournamentCode] = useState('wc2026');
  const [size, setSize] = useState<DrinkSize>('half');
  const [matchNumber, setMatchNumber] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [created, setCreated] = useState<AdminVoucher | null>(null);

  async function submit(e: Event) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      const mn = matchNumber.trim();
      const v = await giftDrinkToUser(accessToken, user.id, {
        tournamentCode: tournamentCode.trim(),
        drinkSize: size,
        matchNumber: mn === '' ? undefined : Number(mn),
      });
      setCreated(v);
    } catch (e: any) {
      setErr(e?.message || `${e}`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div class="admin-overlay" onClick={onClose}>
      <form
        class="admin-modal"
        style={{ width: 'min(480px, 100%)' }}
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
      >
        <h2 style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Avatar url={user.avatarUrl} email={user.email} />
          <span>Gift drink voucher</span>
        </h2>
        <p class="admin-lede" style={{ marginTop: 0 }}>
          Mints a redeemable drink voucher for <strong>{user.email}</strong>{user.nickname ? <> (<em>{user.nickname}</em>)</> : null}.
          Walks the same partner-scan flow as a real prize. Use the
          "Vouchers" button on this row to erase the test activity
          afterwards.
        </p>

        {created ? (
          <div
            style={{
              background: '#0a0705',
              border: '1px solid rgba(91,186,91,0.4)',
              borderRadius: 8,
              padding: 14,
              marginTop: 8,
              fontFamily: 'IBM Plex Mono, Menlo, monospace',
              fontSize: 12,
              color: '#f5ede0',
              whiteSpace: 'pre-wrap',
            }}
          >
            {`voucher #${created.id} issued
size:      ${created.label ?? created.drinkSize}
litres:    ${created.litres ?? '—'}
state:     ${created.state}
qr token:  ${created.qrToken ?? '—'}

Paste the QR token into the partner scanner to walk the redeem flow.`}
          </div>
        ) : (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <label class="admin-field">
                <span>Tournament code</span>
                <input
                  value={tournamentCode}
                  onInput={(e) => setTournamentCode((e.target as HTMLInputElement).value)}
                  autocomplete="off"
                  spellcheck={false}
                />
              </label>
              <label class="admin-field">
                <span>Drink size</span>
                <select value={size} onChange={(e) => setSize((e.target as HTMLSelectElement).value as DrinkSize)}>
                  {DRINK_SIZES.map((d) => (
                    <option key={d.size} value={d.size}>{d.label}</option>
                  ))}
                </select>
              </label>
            </div>
            <label class="admin-field">
              <span>Match number <span style={{ opacity: 0.5 }}>(optional — leave blank to gift without a match)</span></span>
              <input
                type="number"
                min={1}
                value={matchNumber}
                onInput={(e) => setMatchNumber((e.target as HTMLInputElement).value)}
                placeholder="e.g. 12"
              />
            </label>
          </>
        )}

        {err && <p class="admin-error">{err}</p>}

        <div class="admin-modal-actions">
          <button type="button" class="admin-link" onClick={onClose}>
            {created ? 'Close' : 'Cancel'}
          </button>
          {!created && (
            <button type="submit" class="admin-primary" disabled={busy}>
              {busy ? 'Gifting…' : 'Gift drink'}
            </button>
          )}
        </div>
      </form>
    </div>
  );
}

// ─── Vouchers modal — list, erase, unerase ───────────────────────────────
//
// Lists every voucher this user has, including soft-deleted (erased)
// ones so the operator can undo. Each row offers a single action: for
// active vouchers, "Erase" (refunds partner litres / article stock and
// hides the row); for erased vouchers, "Undo erase" (restores the
// prior state and re-applies the side-effect).

function UserVouchersModal({
  user,
  accessToken,
  onClose,
}: {
  user: AdminUser;
  accessToken: string;
  onClose: () => void;
}) {
  const [vouchers, setVouchers] = useState<AdminVoucher[] | null>(null);
  const [busy, setBusy] = useState<number | null>(null); // voucher id currently being mutated
  const [err, setErr] = useState<string | null>(null);

  async function load() {
    setErr(null);
    try {
      setVouchers(await listUserVouchers(accessToken, user.id));
    } catch (e: any) {
      setErr(e?.message || `${e}`);
    }
  }

  useEffect(() => {
    load();
  }, [accessToken, user.id]);

  async function doErase(id: number) {
    setBusy(id);
    setErr(null);
    try {
      const updated = await eraseVoucher(accessToken, id);
      setVouchers((cur) => cur?.map((v) => (v.id === updated.id ? updated : v)) ?? cur);
    } catch (e: any) {
      setErr(e?.message || `${e}`);
    } finally {
      setBusy(null);
    }
  }

  async function doUnerase(id: number) {
    setBusy(id);
    setErr(null);
    try {
      const updated = await unEraseVoucher(accessToken, id);
      setVouchers((cur) => cur?.map((v) => (v.id === updated.id ? updated : v)) ?? cur);
    } catch (e: any) {
      setErr(e?.message || `${e}`);
    } finally {
      setBusy(null);
    }
  }

  return (
    <div class="admin-overlay" onClick={onClose}>
      <div
        class="admin-modal"
        style={{ width: 'min(820px, 100%)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Avatar url={user.avatarUrl} email={user.email} />
          <span>Vouchers · {user.email}</span>
        </h2>
        <p class="admin-lede" style={{ marginTop: 0 }}>
          Soft-deletes the voucher and refunds any litres / stock it
          had consumed. "Già bevuti" and partner remaining_litres
          realign automatically. Undo flips the row back to the prior
          state and re-applies the deduction.
        </p>

        {err && <p class="admin-error">{err}</p>}

        {vouchers == null && !err && <p class="admin-lede">Loading…</p>}
        {vouchers && vouchers.length === 0 && (
          <p class="admin-lede">This user has no vouchers.</p>
        )}

        {vouchers && vouchers.length > 0 && (
          <table class="admin-table" style={{ marginTop: 8 }}>
            <thead>
              <tr>
                <th>ID</th>
                <th>Kind</th>
                <th>State</th>
                <th>Detail</th>
                <th>QR token</th>
                <th>Issued</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {vouchers.map((v) => (
                <tr key={v.id} style={{ opacity: v.state === 'erased' ? 0.55 : 1 }}>
                  <td class="admin-mono">#{v.id}</td>
                  <td class="admin-mono" style={{ fontSize: 11 }}>{v.kind}</td>
                  <td><VoucherStatePill state={v.state} /></td>
                  <td style={{ fontSize: 12 }}>
                    {v.kind === 'drink'
                      ? `${v.label ?? v.drinkSize ?? '—'}${v.matchNumber ? ` · match #${v.matchNumber}` : ' · no match'}${v.partnerName ? ` · @${v.partnerName}` : ''}`
                      : `${v.articleTitle ?? `article #${v.articleId ?? '?'}`}${v.partnerName ? ` · @${v.partnerName}` : ''}`}
                  </td>
                  <td class="admin-mono" style={{ fontSize: 10, maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {v.qrToken ?? '—'}
                  </td>
                  <td class="admin-mono" style={{ fontSize: 11 }}>{fmtDate(v.issuedAt)}</td>
                  <td>
                    {v.state === 'erased' ? (
                      <button
                        type="button"
                        class="admin-link"
                        disabled={busy === v.id}
                        onClick={() => doUnerase(v.id)}
                      >
                        {busy === v.id ? '…' : 'Undo erase'}
                      </button>
                    ) : (
                      <button
                        type="button"
                        class="admin-link"
                        disabled={busy === v.id}
                        onClick={() => doErase(v.id)}
                      >
                        {busy === v.id ? '…' : 'Erase'}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        <div class="admin-modal-actions">
          <button type="button" class="admin-link" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

function VoucherStatePill({ state }: { state: AdminVoucher['state'] }) {
  const color =
    state === 'redeemed' ? '#5BBA5B'
    : state === 'erased' ? '#9EA8B5'
    : state === 'locked' || state === 'void' ? '#C0392B'
    : state === 'awaiting_partner' || state === 'ready_for_pickup' ? '#E8B53C'
    : '#E87B1E';
  return (
    <span
      class="admin-mono"
      style={{
        fontSize: 10,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        padding: '2px 6px',
        borderRadius: 999,
        background: `${color}22`,
        color,
        border: `1px solid ${color}55`,
      }}
    >
      {state.replace(/_/g, ' ')}
    </span>
  );
}
